import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {MATERIAL_REQUEST_ITEM_MODEL, MATERIAL_REQUEST_MODEL} from "../Model/Model";
import HandleChange from "../../../../Helpers/HandleChange";
import {GetMeasurementUnits} from "../../../../reduxToolkit/FoodieIngredient/IngredientApi";
import {PHOTO_PICKER_MODEL} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import routes from "../../../../Routes/routes";
import {CreateMaterialRequest} from "../../../../reduxToolkit/MaterialRequest/MaterialRequestApi";
import {errorMsg} from "../../../../Apis/functions";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 */
const CreateMaterialRequestApp = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const { measurementUnitsAll } = useSelector((state) => state.IngredientSlice);
    const [emptyFields, setEmptyFields] = useState(false);
    const [emptyFieldsItems, setEmptyFieldsItems] = useState(false);
    const [materialRequest, setMaterialRequest] = useState({ ...MATERIAL_REQUEST_MODEL });
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL });
    const { handleChange } = HandleChange({ model: materialRequest, setModel: setMaterialRequest });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLevelId, setSelectedLevelId] = useState(null);
    const [selectedEmployees, setSelectedEmployees] = useState([]);

    /**
     * Handles changes for dynamically created material request items.
     *
     * Updates the corresponding item field value based on the provided index.
     *
     * @param {Object} e - Input change event.
     * @param {number} index - Index of the item being modified.
     *
     * @returns {void}
     */
    const handleChangeTemp = (e, index) => {
        const { name, value } = e.target;

        setMaterialRequest((prev) => {
            const items = [...prev.items];

            items[index] = {
                ...items[index],
                [name]: value,
            };

            return {
                ...prev,
                items,
            };
        });
    };

    /**
     * Loads measurement units on component initialization.
     * The measurement units are used to populate item unit selectors
     * within the material request form.
     */
    useEffect(() => {
        dispatch(GetMeasurementUnits());
    }, [dispatch]);

    /**
     * Adds a new empty item to the material request.
     *
     * Before adding a new item, validates that all existing items
     * contain the required information.
     *
     * If validation fails:
     * - Displays item validation errors.
     *
     * If validation succeeds:
     * - Appends a new item initialized from MATERIAL_REQUEST_ITEM_MODEL.
     *
     * @returns {void}
     */
    const handleAdd = () => {
        if(!materialRequest?.isValidItems()){
            setEmptyFieldsItems(true);
            return;
        }
        setEmptyFieldsItems(false);
        setMaterialRequest(prev => ({
            ...prev,
            items: [...prev.items, { ...MATERIAL_REQUEST_ITEM_MODEL, id: `${Date.now()}-${Math.random()}` }]
        }));
    };

    /**
     * Removes an item from the material request item list.
     *
     * Displays a success notification after deletion.
     *
     * @param {Object} item - Item to remove from the request.
     *
     * @returns {void}
     */
    const handleDeleteItem = (item) => {
        setMaterialRequest((prev) => ({
            ...prev,
            items: prev.items.filter((currentItem) => currentItem.id !== item.id),
        }));
        toast.success(t('has_been_deleted_successfully'));
    };

    /**
     * Handles the material request creation process.
     *
     * Validates:
     * - General form fields.
     * - Material request items.
     * - Selected employees.
     * - Uploaded evidence files.
     *
     * If validation fails:
     * - Displays validation warnings.
     *
     * If validation succeeds:
     * - Sends the request payload to the backend.
     * - Displays success feedback.
     * - Redirects the user back to the material request list.
     *
     * @returns {void}
     */
    const handleAction = () => {
        if (!materialRequest.isValid({ photoPicker, selectedEmployees }) || !materialRequest?.isValidItems()) {
            setEmptyFields(true);
            setEmptyFieldsItems(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        dispatch(CreateMaterialRequest(materialRequest.toRegister({ selectedEmployees, files: photoPicker.listFiles}))).then(({payload:{data:{success, code}}})=>{
            if (success){
                toast.success(t('has_been_created_successfully'));
                handleOnBack();
            }else errorMsg(code)
        });
    };

    /**
     * Navigates back to the material request main page.
     *
     * @returns {void}
     */
    const handleOnBack = () => {
        navigate(routes.materialRequest.main);
    };

    /**
     * Resets the material request form to its initial state.
     *
     * Clears:
     * - General request information.
     * - Uploaded files.
     * - Selected images.
     * - Item list data.
     *
     * @returns {void}
     */
    const resetForm = () => {
        setMaterialRequest({ ...MATERIAL_REQUEST_MODEL });
        setPhotoPicker({ ...PHOTO_PICKER_MODEL });
    };

    return {
        t,
        DEV,
        permission,
        navigate,
        emptyFields,
        emptyFieldsItems,
        materialRequest,
        handleChangeTemp,
        handleChange,
        photoPicker,
        setPhotoPicker,
        showDeleteModal,
        setShowDeleteModal,
        selectedLevelId,
        setSelectedLevelId,
        selectedEmployees,
        setSelectedEmployees,
        measurementUnitsAll,
        handleAdd,
        handleDeleteItem,
        resetForm,
        handleOnBack,
        handleAction
    };
};

export default CreateMaterialRequestApp;
